import React from "react";
import { Link } from "react-router-dom";
import van from "../assets/images/van.jpeg";

/* JS validation regex (not in pattern=) */
const NAME_RE = /^[A-Za-z' -]{2,60}$/u;         // letters, spaces, apostrophes, hyphens
const PHONE_RE = /^[0-9+()\- ]{7,20}$/;         // digits and common separators

/* ── Simple SEO head ───────────────────────────────────────────── */
function SEO() {
  const title = "Plumbers in Lincoln | Beaumonts Plumbing & Heating";
  const description =
    "Professional plumbers in Lincoln. Boilers, bathrooms, repairs and maintenance with clear pricing and neat workmanship.";
  const url = typeof window !== "undefined" ? window.location.origin : "";
  const href = typeof window !== "undefined" ? window.location.href : "";
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Plumber",
    name: "Beaumonts Plumbing & Heating",
    url,
    image: "/assets/images/van.jpeg",
    telephone: "+44 1522 692592",
    email: "mailto:ian@beaumontsplumbing.co.uk",
    areaServed: ["Lincoln", "Lincolnshire", "LN1", "LN2", "LN3", "LN4", "LN5", "LN6"],
    address: { "@type": "PostalAddress", addressLocality: "Lincoln", addressRegion: "Lincolnshire", addressCountry: "GB" },
    priceRange: "££",
  };

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={href} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={href} />
      <meta property="og:image" content={`${url}${van}`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${url}${van}`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </>
  );
}

type SubmitState = "idle" | "submitting" | "success" | "error";

export default function Home() {
  const [status, setStatus] = React.useState<SubmitState>("idle");
  const [errorMsg, setErrorMsg] = React.useState<string>("");
  const alertRef = React.useRef<HTMLDivElement | null>(null);

  function validateAndReport(form: HTMLFormElement) {
    const nameEl = form.elements.namedItem("name") as HTMLInputElement;
    const phoneEl = form.elements.namedItem("phone") as HTMLInputElement;
    const emailEl = form.elements.namedItem("email") as HTMLInputElement;

    nameEl.setCustomValidity("");
    phoneEl.setCustomValidity("");
    emailEl.setCustomValidity("");

    const name = nameEl.value.trim();
    const phone = phoneEl.value.trim();
    const email = emailEl.value.trim();

    if (!name) {
      nameEl.setCustomValidity("Please enter your name.");
    } else if (!NAME_RE.test(name)) {
      nameEl.setCustomValidity("Use letters, spaces, apostrophes or hyphens (2–60 characters).");
    }

    if (!phone) {
      phoneEl.setCustomValidity("Please enter a phone number.");
    } else if (!PHONE_RE.test(phone)) {
      phoneEl.setCustomValidity("UK number, e.g. 01522 692592 or +44 1522 692592.");
    }

    if (!email) {
      emailEl.setCustomValidity("Please enter an email address.");
    } else if (!emailEl.checkValidity()) {
      // type="email" native check
      emailEl.setCustomValidity("Enter a valid email address.");
    }

    // Trigger native tooltips if any invalid
    if (!form.reportValidity()) return false;
    return true;
  }

  async function handleQuickSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorMsg("");
    const form = e.currentTarget;

    if (!validateAndReport(form)) return;

    const fd = new FormData(form);
    const payload = {
      name: String(fd.get("name") || "").trim(),
      email: String(fd.get("email") || "").trim(),
      phone: String(String(fd.get("phone") || "")).replace(/\s+/g, ""),
      message: String(fd.get("message") || "").trim() || "-",
      company: String(fd.get("company") || "").trim(), // honeypot
      source: "home-quick",
    };

    setStatus("submitting");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));

      setStatus("success");
      form.reset();
      requestAnimationFrame(() => alertRef.current?.focus());
    } catch (err: any) {
      setStatus("error");
      setErrorMsg(err?.message || "Send failed. Please try again.");
      requestAnimationFrame(() => alertRef.current?.focus());
    }
  }

  const isBusy = status === "submitting";

  return (
    <>
      <SEO />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <img
          src={van}
          alt="Beaumonts Plumbing & Heating van"
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/75 to-white/30 lg:from-white/90 lg:via-white/65 lg:to-white/20" />

        <div className="relative mx-auto max-w-7xl px-3 sm:px-6 py-12 md:py-16 lg:py-24 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <h1 className="font-semibold tracking-[-0.02em] text-[clamp(26px,6vw,44px)] leading-tight md:text-5xl md:leading-tight">
              Plumbers in <span className="text-neutral-800">Lincolnshire</span>, done properly.
            </h1>
            <p className="mt-4 md:mt-5 text-[15px] md:text-base text-neutral-800/90 max-w-2xl">
              Boilers, bathrooms, leaks and heating — specified, installed and maintained with clear pricing,
              punctual arrival, and a clean handover. Work you don’t have to chase.
            </p>

            <div className="mt-6 md:mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center rounded-md bg-[#3bb3ef] text-white px-4 py-2.5 text-[13px] md:text-sm font-medium hover:brightness-110"
              >
                Request a quote
              </Link>
              <a
                href="tel:+441522692592"
                className="inline-flex items-center rounded-md border border-neutral-300 bg-white/70 backdrop-blur px-4 py-2.5 text-[13px] md:text-sm font-medium hover:bg-white"
              >
                Call 01522 692592
              </a>
            </div>

            <div className="mt-4 md:mt-6 text-[13px] md:text-sm text-neutral-800/90">
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1 sm:gap-2">
                <a href="mailto:ian@beaumontsplumbing.co.uk" className="underline underline-offset-4 decoration-[#3bb3ef]">
                  ian@beaumontsplumbing.co.uk
                </a>
                <span className="hidden sm:inline text-neutral-400">•</span>
                <span>
                  Mobile{" "}
                  <a href="tel:+447803608738" className="hover:text-neutral-900">
                    07803 608738
                  </a>
                </span>
              </div>
            </div>
          </div>

          {/* Right: contact card */}
          <aside className="lg:col-span-5">
            <div className="rounded-2xl border border-neutral-200 bg-white/90 backdrop-blur p-5 md:p-6 shadow-lg max-w-md mx-auto lg:max-w-none lg:mx-0">
              {status === "success" ? (
                <div className="text-center py-6" ref={alertRef} tabIndex={-1} aria-live="polite">
                  <h2 className="text-lg font-semibold">Thank you</h2>
                  <p className="mt-2 text-sm text-neutral-700">
                    We’ve received your enquiry and will be in touch shortly.
                  </p>
                  <div className="mt-4 text-sm text-neutral-700">
                    Prefer to talk?{" "}
                    <a href="tel:+441522692592" className="underline">
                      01522 692592
                    </a>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="text-base md:text-lg font-semibold">Tell us what you need</h2>
                  <p className="mt-1 text-[13px] md:text-sm text-neutral-700">We’ll reply with times and pricing.</p>

                  <div ref={alertRef} tabIndex={-1} aria-live="polite" className="mt-3 outline-none">
                    {status === "error" && (
                      <div className="rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">
                        Something went wrong. {errorMsg || "Please try again."}
                      </div>
                    )}
                  </div>

                  {/* Quick form posts to /api/contact */}
                  <form onSubmit={handleQuickSubmit} className="mt-4 grid gap-3" noValidate>
                    {/* Honeypot */}
                    <input type="text" name="company" autoComplete="off" tabIndex={-1} className="hidden" aria-hidden="true" />

                    <input
                      name="name"
                      placeholder="Your name"
                      className="border border-neutral-300 rounded px-3 py-2 text-[13px] md:text-sm"
                      autoComplete="name"
                      inputMode="text"
                      maxLength={80}
                      required
                      disabled={isBusy}
                    />
                    <input
                      name="phone"
                      placeholder="Phone"
                      className="border border-neutral-300 rounded px-3 py-2 text-[13px] md:text-sm"
                      autoComplete="tel"
                      inputMode="tel"
                      maxLength={20}
                      required
                      disabled={isBusy}
                    />
                    <input
                      name="email"
                      type="email"
                      placeholder="Email"
                      className="border border-neutral-300 rounded px-3 py-2 text-[13px] md:text-sm"
                      autoComplete="email"
                      inputMode="email"
                      maxLength={254}
                      required
                      disabled={isBusy}
                    />
                    <textarea
                      name="message"
                      placeholder="Brief description"
                      className="border border-neutral-300 rounded px-3 py-2 text-[13px] md:text-sm min-h-24"
                      maxLength={2000}
                      disabled={isBusy}
                    />

                    <button
                      type="submit"
                      disabled={isBusy}
                      className={`mt-2 inline-flex items-center justify-center rounded-md bg-[#3bb3ef] text-white px-4 py-2.5 text-[13px] md:text-sm font-medium ${
                        isBusy ? "opacity-75 cursor-not-allowed" : "hover:brightness-110"
                      }`}
                    >
                      {isBusy ? "Sending…" : "Request a callback"}
                    </button>

                    <p className="text-[11px] md:text-[12px] text-neutral-600">
                      We’ll only use your details to respond to your enquiry.
                    </p>
                  </form>
                </>
              )}
            </div>
          </aside>
        </div>
      </section>

      {/* HOW WE HELP — premium cards */}
      <section className="relative overflow-hidden py-14 md:py-20 bg-white border-t border-neutral-200">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(70%_40%_at_50%_0%,#f4f7fb_0%,transparent_60%)]" />
        <div className="relative mx-auto max-w-7xl px-3 sm:px-6">
          <div className="max-w-2xl">
            <p className="text-xs font-medium tracking-widest uppercase text-[#3bb3ef]">Our services</p>
            <h2 className="mt-2 text-2xl md:text-4xl font-semibold tracking-tight">
              Practical work, done neatly and on schedule
            </h2>
            <p className="mt-3 text-[15px] md:text-base text-neutral-700">
              We survey, explain your options, price it clearly, then turn up and get it sorted.
            </p>
          </div>

          <div className="mt-8 md:mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            <article className="group relative rounded-2xl border border-neutral-200 bg-white p-5 md:p-6 shadow-[0_1px_0_0_rgba(0,0,0,0.03)] hover:shadow-md hover:-translate-y-0.5 transition-all">
              <span className="absolute inset-x-0 top-0 h-1 rounded-t-2xl bg-gradient-to-r from-[#3bb3ef] via-[#1f8fd3] to-[#b40016]" />
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#3bb3ef]/10 text-[#1f8fd3]">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M6 22h12v-2H6v2zm1-4h10l1-9h-3V5a3 3 0 10-6 0v4H6l1 9zm5-15a1 1 0 011 1v4h-2V4a1 1 0 011-1z"/>
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-semibold">
                Boiler, Cylinder and Air Source Installation &amp; servicing.
              </h3>
              <p className="mt-2 text-sm text-neutral-700">
                We size the system properly, explain the options and look after it once it’s in.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-neutral-700">
                <li className="flex items-start gap-2"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#3bb3ef]" /> Fixed, itemised quotes</li>
                <li className="flex items-start gap-2"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#3bb3ef]" /> Neat pipework and clean finishes</li>
                <li className="flex items-start gap-2"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#3bb3ef]" /> Servicing and aftercare</li>
              </ul>
              <a href="/services#boilers" className="mt-5 inline-flex text-sm font-medium text-[#1f8fd3] hover:underline">
                Learn more
              </a>
            </article>

            <article className="group relative rounded-2xl border border-neutral-200 bg-white p-5 md:p-6 shadow-[0_1px_0_0_rgba(0,0,0,0.03)] hover:shadow-md hover:-translate-y-0.5 transition-all">
              <span className="absolute inset-x-0 top-0 h-1 rounded-t-2xl bg-gradient-to-r from-[#3bb3ef] via-[#1f8fd3] to-[#b40016]" />
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#3bb3ef]/10 text-[#1f8fd3]">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M4 10h16v10H4zM6 4h12v4H6zM8 12h4v6H8z"/>
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-semibold">Bathrooms</h3>
              <p className="mt-2 text-sm text-neutral-700">
                One reliable team from first fix to final silicone. We keep it tidy and on time.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-neutral-700">
                <li className="flex items-start gap-2"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#3bb3ef]" /> Layout and specification help</li>
                <li className="flex items-start gap-2"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#3bb3ef]" /> Coordinated trades and clear dates</li>
                <li className="flex items-start gap-2"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#3bb3ef]" /> Clean handover you can live with</li>
              </ul>
              <a href="/services#bathrooms" className="mt-5 inline-flex text-sm font-medium text-[#1f8fd3] hover:underline">
                Learn more
              </a>
            </article>

            <article className="group relative rounded-2xl border border-neutral-200 bg-white p-5 md:p-6 shadow-[0_1px_0_0_rgba(0,0,0,0.03)] hover:shadow-md hover:-translate-y-0.5 transition-all">
              <span className="absolute inset-x-0 top-0 h-1 rounded-t-2xl bg-gradient-to-r from-[#3bb3ef] via-[#1f8fd3] to-[#b40016]" />
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#3bb3ef]/10 text-[#1f8fd3]">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M3 4h18v2H3zM4 8h16v10H4zM6 10h5v6H6zM13 10h5v6h-5z"/>
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-semibold">First fix and second fix</h3>
              <p className="mt-2 text-sm text-neutral-700">
                New build or refurbishment, we work to the drawings and keep the site safe and tidy.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-neutral-700">
                <li className="flex items-start gap-2"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#3bb3ef]" /> Pipework set-out and pressure tests</li>
                <li className="flex items-start gap-2"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#3bb3ef]" /> Sanitaryware, radiators and controls</li>
                <li className="flex items-start gap-2"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#3bb3ef]" /> Snag list closed before handover</li>
              </ul>
              <a href="/services#firstfix" className="mt-5 inline-flex text-sm font-medium text-[#1f8fd3] hover:underline">
                Learn more
              </a>
            </article>
          </div>

          {/* Guarantees strip */}
          <div className="mt-10 md:mt-12 grid sm:grid-cols-3 gap-3 text-sm">
            {[
              ["Clear, itemised pricing", "You know the cost before we start"],
              ["Qualified and insured", "Proper paperwork and accountability"],
              ["Aftercare that picks up", "Help when you need it"],
            ].map(([title, sub]) => (
              <div key={title} className="rounded-lg border border-neutral-200 bg-white p-4 flex items-start gap-3">
                <span className="mt-1 h-2.5 w-2.5 rounded-full bg-[#b40016]" />
                <div>
                  <div className="font-medium">{title}</div>
                  <div className="text-neutral-600">{sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
